import React, { useState, useRef } from 'react';
import * as api from '../services/apiService';

const XteViewer = ({ session, supabase }) => {
  const [xteFiles, setXteFiles] = useState([]);
  const [loading, setLoading] = useState({
    processing: false,
    downloadingExcel: false,
    downloadingCsv: false,
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({
    step1: { status: 'pending', message: 'Aguardando...' },
    step2: { status: 'pending', message: 'Pendente...' },
    step3: { status: 'pending', message: 'Pendente...' }
  });

  const fileRef = useRef(null);

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    return session.access_token;
  };

  const downloadFile = (blob, defaultFilename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = defaultFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const resetState = () => {
    setError('');
    setSuccessMessage('');
    setResults(null);
    setProcessingStatus({
      step1: { status: 'pending', message: 'Aguardando...' },
      step2: { status: 'pending', message: 'Pendente...' },
      step3: { status: 'pending', message: 'Pendente...' }
    });
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleFileChange = (e) => {
    setXteFiles(Array.from(e.target.files));
    resetState();
  };

  const handleDrag = (active) => {
    setDragActive(active);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.xte'));
    if (files.length > 0) {
      setXteFiles(files);
      resetState();
    }
  };

  const clearFiles = () => {
    setXteFiles([]);
    if (fileRef.current) fileRef.current.value = '';
    resetState();
  };

  const handleProcess = async () => {
    if (xteFiles.length === 0) {
      setError('Por favor, selecione pelo menos um arquivo XTE.');
      return;
    }

    resetState();
    setLoading(prev => ({ ...prev, processing: true }));

    try {
      const token = await getAuthToken();

      setProcessingStatus(prev => ({
        ...prev,
        step1: { status: 'processing', message: 'Lendo arquivos...' }
      }));
      
      const data = await api.consolidateXteFilesAPI(xteFiles, token);
      
      setProcessingStatus(prev => ({
        ...prev,
        step1: { status: 'success', message: `${xteFiles.length} arquivos lidos com sucesso.` },
        step2: { status: 'success', message: `${data.data.total_records} registros consolidados.` },
        step3: { status: 'success', message: 'Dados prontos para download.' }
      }));

      setResults(data);
      showSuccess('Arquivos processados com sucesso!');

    } catch (error) {
      setError(error.message || 'Erro ao processar arquivos');
      setProcessingStatus(prev => ({
        ...prev,
        step1: { status: 'error', message: 'Erro na leitura dos arquivos' }
      }));
    } finally {
      setLoading(prev => ({ ...prev, processing: false }));
    }
  };

  const handleDownloadExcel = async () => {
    setLoading(prev => ({ ...prev, downloadingExcel: true }));
    try {
      const token = await getAuthToken();
      const blob = await api.consolidateXteFilesExcelAPI(xteFiles, token);
      downloadFile(blob, `dados_consolidados_TISS_${Date.now()}.xlsx`);
      showSuccess('Arquivo Excel gerado com sucesso!');
    } catch (error) {
      setError(error.message || 'Erro ao gerar arquivo Excel');
    } finally {
      setLoading(prev => ({ ...prev, downloadingExcel: false }));
    }
  };

  const handleDownloadCsv = async () => {
    setLoading(prev => ({ ...prev, downloadingCsv: true }));
    try {
      const token = await getAuthToken();
      const blob = await api.consolidateXteFilesCsvAPI(xteFiles, token);
      downloadFile(blob, `dados_consolidados_TISS_${Date.now()}.csv`);
      showSuccess('Arquivo CSV gerado com sucesso!');
    } catch (error) {
      setError(error.message || 'Erro ao gerar arquivo CSV');
    } finally {
      setLoading(prev => ({ ...prev, downloadingCsv: false }));
    }
  };

  const isAnyLoading = Object.values(loading).some(Boolean);

  // Adicionar lista de colunas a serem excluídas do preview (não altera results)
  const excludedColumns = [
    'guiaMonitoramento',
    'dadosContratadoExecutante',
    'dadosBeneficiario',
    'identBeneficiario',
    'formasRemuneracao',
    'valoresGuia',
    'procedimentos',
    'identProcedimento',
    'Procedimento',
    'diagnosticoCID10',
    'dataRealizacao_dt',
    'dataNascimento_dt'
  ];

  // Colunas que serão exibidas no preview (filtradas)
  const previewColumns = results?.data?.columns
    ? results.data.columns.filter(col => !excludedColumns.includes(col))
    : [];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {(error || successMessage) && (
        <div className="fixed top-24 right-4 z-50 max-w-md animate-slide-in-right">
          {error && (
            <div className="alert-error shadow-xl">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-semibold">Erro</p>
                  <p className="text-sm">{error}</p>
                </div>
                <button onClick={() => setError('')} className="ml-auto">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="alert-success shadow-xl">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-semibold">Sucesso</p>
                  <p className="text-sm">{successMessage}</p>
                </div>
                <button onClick={() => setSuccessMessage('')} className="ml-auto">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card-glass p-8 text-center">
        <div className="inline-block p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-4">
          <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">
          Conversor XTE/Excel
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Transforme seus arquivos .XTE em dados estruturados e faça análises detalhadas
        </p>
      </div>
    
      <div className="card p-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Selecione os Arquivos
        </h3>
        
        <div
          className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
            dragActive 
              ? 'border-blue-500 bg-blue-100 scale-105' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onDragEnter={() => handleDrag(true)}
          onDragLeave={() => handleDrag(false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".xte"
            onChange={handleFileChange}
            className="hidden"
            id="xte-files"
          />
          <label htmlFor="xte-files" className="cursor-pointer">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-2">
              Arraste arquivos aqui ou clique para selecionar
            </p>
            <p className="text-sm text-gray-500">
              Formatos aceitos: <span className="font-semibold">.XTE</span>
            </p>
          </label>
        </div>

        {xteFiles.length > 0 && (
          <div className="mt-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="badge-primary">{xteFiles.length}</span>
                arquivo(s) selecionado(s)
              </p>
              <button
                onClick={clearFiles}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-2 group"
              >
                <svg className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Limpar seleção
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2 bg-gray-50 rounded-xl p-4">
              {xteFiles.map((file, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <div className="badge-success">XTE</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={handleProcess}
            disabled={isAnyLoading || xteFiles.length === 0}
            className="btn btn-primary btn-lg w-full"
          >
            {loading.processing ? (
              <>
                <div className="loading-spinner" />
                <span>Processando arquivos...</span>
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Processar {xteFiles.length} arquivo(s)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {(loading.processing || results) && (
        <div /> 
      )}

      {results && (
        <div className="space-y-6 animate-scale-in">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-3xl">📁</span>
                </div>
                <div>
                  <p className="text-4xl font-bold text-blue-600">{results.data.files_processed}</p>
                  <p className="text-sm font-medium text-blue-700">Arquivos Processados</p>
                </div>
              </div>
            </div>
            <div className="card p-8 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-3xl">📊</span>
                </div>
                <div>
                  <p className="text-4xl font-bold text-green-600">{results.data.total_records}</p>
                  <p className="text-sm font-medium text-green-700">Registros Consolidados</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Downloads Disponíveis
            </h3>
            <div className="grid md:grid-cols-1 gap-4">
              <button
                onClick={handleDownloadExcel}
                disabled={isAnyLoading}
                className="btn btn-success btn-lg"
              >
                {loading.downloadingExcel ? (
                  <>
                    <div className="loading-spinner" />
                    <span>Gerando Excel...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Baixar Excel (.xlsx)</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {results.data.records.length > 0 && (
            <div className="card overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  <div>
                    <h3 className="text-xl font-bold">Preview dos Dados</h3>
                    <p className="text-blue-100 text-sm">Primeiras 10 linhas do total de {results.data.records.length} registros</p>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      {previewColumns.map(col => (
                        <th key={col} className="table-header">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.data.records.slice(0, 10).map((record, idx) => (
                      <tr key={idx} className="table-row">
                        {previewColumns.map(col => (
                          <td key={col} className="table-cell">
                            {(record && (record[col] !== undefined && record[col] !== null)) ? record[col] : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatusCard = ({ title, status, message, icon }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success': return 'status-card-success';
      case 'processing': return 'status-card-processing';
      case 'error': return 'status-card-error';
      default: return 'status-card-pending';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'success': return '✅';
      case 'processing': return '⏳';
      case 'error': return '❌';
      default: return icon;
    }
  };

  return (
    <div className={`${getStatusColor()} p-6`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl">{getIcon()}</span>
        <h4 className="font-bold text-gray-800">{title}</h4>
      </div>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );
};

export default XteViewer;
